import { Flex, FlexProps, Box } from '@cad-arc';
import { autorun } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getMapKey } from '../../../esri/helpers/utils';
import { LocationStoreView } from '../../types/locations';
import { useMapboxMap } from '../../hooks/use-mapbox-map';
import mapboxgl from 'mapbox-gl';
import { LocationDisplayProps } from './types';
import { LocationDetails } from './location-details';
import { LocationMapContainer } from './location-map-container';

/**
 * @deprecated Use EsriLocationMap
 */
export const MapboxLocationMap = observer(
    ({
        store,
        isLoadingResults,
        query,
        onClickRow,
        moveFocusNext,
        inputRef,
        closePopover,
        showLocationDetails,
        showMapKey,
        isCliMode,
        size,
    }: LocationDisplayProps) => {
        const { list } = store.data;
        // Property access for mobx update. (required, Otherwise mobx doesn't update and state doesn't change)
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        list.length;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        store.highlightedLocationIndex;

        const mapboxMapContainerRef = useRef<HTMLDivElement | null>(null);
        const mapboxMapMarkersRef = useRef<InstanceType<(typeof mapboxgl)['Marker']>[]>([]);
        const [selectedLocation, setSelectedLocation] = useState<LocationStoreView | null>(null);
        const [showUnverifiedAddressIndicator, setShowUnverifiedAddressIndicator] = useState(false);

        const mapboxMapControls = useMapboxMap({
            mapContainerRef: mapboxMapContainerRef,
            centerPoint: {
                latitude: store.data.list[0]?.latitude ?? 0,
                longitude: store.data.list[0]?.longitude ?? 0,
            },
        });

        /**
         * We do this reactively so that the map can update fully, then pan.
         * When marker updates are in progress, mapViewController becomes undefined.
         */
        useEffect(
            function panToSelectedLocation() {
                if (
                    mapboxMapControls &&
                    selectedLocation !== null &&
                    selectedLocation.longitude &&
                    selectedLocation.latitude
                ) {
                    mapboxMapControls.current?.panTo(
                        [selectedLocation.longitude, selectedLocation.latitude],
                        { animate: false }
                    );
                }
            },
            [selectedLocation, mapboxMapControls]
        );

        const onLocationSelected = useCallback(
            function onLocationSelected(
                location: LocationStoreView,
                index: number,
                userInputEvent?: { source: 'keyboard' | 'mouse' }
            ): void {
                store.setHighlightedLocationIndex(index, userInputEvent);
                if (location.isForcedResult) {
                    setShowUnverifiedAddressIndicator(true);
                } else {
                    setShowUnverifiedAddressIndicator(false);
                    if (location.longitude && location.latitude) {
                        mapboxMapControls.current?.panTo([location.longitude, location.latitude], {
                            animate: false,
                        });
                    }

                    setSelectedLocation(location);
                }
            },
            [mapboxMapControls, store]
        );

        useEffect(
            function updateMapMarkersAndSelectedLocation() {
                const disposer = autorun(() => {
                    if (mapboxMapControls) {
                        if (mapboxMapMarkersRef.current) {
                            mapboxMapMarkersRef.current.forEach((marker) => {
                                marker.remove();
                            });
                        }
                        store.data.list.forEach((locationItem, index) => {
                            if (
                                locationItem.latitude &&
                                locationItem.longitude &&
                                mapboxMapControls.current
                            ) {
                                const el = document.createElement('div');
                                el.id = locationItem.uuid;
                                el.className =
                                    index === store.highlightedLocationIndex
                                        ? 'selected-map-marker'
                                        : 'default-map-marker';
                                el.style.zIndex =
                                    index === store.highlightedLocationIndex ? '10' : '1';
                                el.textContent = getMapKey(index);
                                el.onclick = () => {
                                    store.setSelectedLocation(locationItem);
                                    closePopover?.();
                                };
                                const marker = new mapboxgl.Marker(el)
                                    .setLngLat([locationItem.longitude, locationItem.latitude])
                                    .addTo(mapboxMapControls.current);
                                mapboxMapMarkersRef.current.push(marker);
                            }
                        });
                        if (store.highlightedLocationIndex !== null) {
                            const selected = store.data.list[store.highlightedLocationIndex];
                            if (selected !== undefined) {
                                onLocationSelected(selected, store.highlightedLocationIndex);
                            }
                        }
                    }
                });
                return () => disposer();
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            []
        );

        // If/when the CLI allows a smaller minimum width than we currently do,
        // we'll need to adjust this to switch the flex dir to row instead of column
        // and adjust other things accordingly. Currently cli v2 imposes
        // a higher minimum width than v1 uses to switch the layout.
        const mapAndDetailsProps: FlexProps = showLocationDetails
            ? {
                  h: 'full',
                  flexDirection: 'column',
              }
            : { h: 'full' };

        return (
            <LocationMapContainer
                isLoadingResults={isLoadingResults}
                onLocationSelected={onLocationSelected}
                query={query}
                showUnverifiedAddressIndicator={showUnverifiedAddressIndicator}
                store={store}
                inputRef={inputRef}
                moveFocusNext={moveFocusNext}
                onClickRow={onClickRow}
                showMapKey={showMapKey}
                isCliMode={isCliMode}
                size={size}
            >
                <Flex {...mapAndDetailsProps} w="full">
                    <Box
                        w="100%"
                        h="100%"
                        ref={mapboxMapContainerRef}
                        style={{ display: showUnverifiedAddressIndicator ? 'none' : undefined }}
                    />
                    {showLocationDetails ? (
                        <LocationDetails selectedLocation={selectedLocation} size={size} />
                    ) : null}
                </Flex>
            </LocationMapContainer>
        );
    }
);
